import React, { useState, useEffect } from "react";
import "../viewcourt/viewcourt.css";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import {
  fetchPitches,
  selectPitchStatus,
  selectPitchError,
  addPitch,
  deletePitch,
} from "../../redux/viewPitchSlice";
import {
  fetchFacilitiesForFilters,
  fetchSportsForFilters,
  setSelectedFacility,
  setSelectedSport,
  selectFacilities,
  selectSports,
  selectSelectedFacility,
  selectSelectedSport,
  selectLoading,
} from "../../redux/slices/pitchFiltersSlice";
import { editPitchService } from "../../services/api";
import sportImg from "../../assets/svgs/football.svg";
import EditIcon from "../../assets/svgs/editbtn.svg";
import TrashIcon from "../../assets/svgs/trash.svg";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import sportIcons from "../facilities/addfacility/sportsicon";

// SkeletonPitchCard component
const SkeletonPitchCard = () => {
  return (
    <Col lg={3} md={6} sm={12}>
      <Card className="pitch-card">
        <Card.Body>
          <div className="d-flex justify-content-between mb-2">
            <div className="pitch-info">
              <Skeleton width={120} height={20} style={{ marginBottom: '4px' }} />
              <Skeleton width={100} height={16} />
            </div>
            <div className="d-flex flex-column align-items-center">
              <div className="sport-icon-container">
                <Skeleton circle width={40} height={40} />
              </div>
              <Skeleton width={60} height={16} style={{ marginTop: '4px', borderRadius: '12px' }} />
            </div>
          </div>
          <div className="action-buttons" style={{ marginTop: '16px' }}>
            <Skeleton width={80} height={32} style={{ marginRight: '8px', borderRadius: '8px' }} />
            <Skeleton width={80} height={32} style={{ borderRadius: '8px' }} />
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

const ViewPitch = () => {
  const dispatch = useDispatch();

  // Get all required data from Redux store
  const pitches = useSelector((state) => state.pitches.pitches);
  const facilities = useSelector(selectFacilities);
  const sports = useSelector(selectSports);
  const selectedFacility = useSelector(selectSelectedFacility);
  const selectedSport = useSelector(selectSelectedSport);
  const loading = useSelector(selectLoading);
  const status = useSelector(selectPitchStatus);
  const error = useSelector(selectPitchError);

  // Separate states for main page filters
  const [mainPageFacility, setMainPageFacility] = useState("");
  const [mainPageSport, setMainPageSport] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    facilityId: "",
    sportId: "",
    name: "",
    maxPlayers: "",
  });
  const [editFormData, setEditFormData] = useState({
    _id: "",
    facilityId: "",
    sportId: "",
    name: "",
    maxPlayers: "",
    facilityName: "",
    sportName: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Adjusted to 8 to better fit 4x2 grid

  // Fetch all required data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          dispatch(fetchPitches()),
          dispatch(fetchFacilitiesForFilters()),
          dispatch(fetchSportsForFilters()),
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    console.log("Pitches from Redux:", pitches);
  }, [pitches]);

  // Update handleFacilityChange for main page
  const handleMainPageFacilityChange = (e) => {
    const facilityId = e.target.value;
    setMainPageFacility(facilityId);
    setMainPageSport("");
  };

  // Update handleSportChange for main page
  const handleMainPageSportChange = (e) => {
    const sportId = e.target.value;
    setMainPageSport(sportId);
  };

  // Handle modal facility and sport changes
  const handleModalFacilityChange = (e) => {
    const facilityId = e.target.value;
    setFormData((prev) => ({ ...prev, facilityId, sportId: "" }));
  };

  const handleModalSportChange = (e) => {
    const sportId = e.target.value;
    setFormData((prev) => ({ ...prev, sportId }));
  };

  const handleEditFacilityChange = (e) => {
    const facilityId = e.target.value;
    setEditFormData((prev) => ({ ...prev, facilityId, sportId: "" }));
  };

  const handleEditSportChange = (e) => {
    const sportId = e.target.value;
    setEditFormData((prev) => ({ ...prev, sportId }));
  };

  // Get filtered sports
  const getMainPageFilteredSports = () => {
    if (!mainPageFacility) return sports;
    const facility = facilities.find((f) => f._id === mainPageFacility);
    return facility?.availableSports
      ? sports.filter((sport) =>
        facility.availableSports.some(
          (availableSport) => availableSport._id === sport._id
        )
      )
      : [];
  };

  const getModalFilteredSports = () => {
    if (!formData.facilityId) return sports;
    const facility = facilities.find((f) => f._id === formData.facilityId);
    return facility?.availableSports
      ? sports.filter((sport) =>
        facility.availableSports.some(
          (availableSport) => availableSport._id === sport._id
        )
      )
      : [];
  };

  const getEditModalFilteredSports = () => {
    if (!editFormData.facilityId) return sports;
    const facility = facilities.find((f) => f._id === editFormData.facilityId);
    return facility?.availableSports
      ? sports.filter((sport) =>
        facility.availableSports.some(
          (availableSport) => availableSport._id === sport._id
        )
      )
      : [];
  };

  // Get filtered pitches
  const getFilteredPitches = () => {
    let filtered = Array.isArray(pitches) ? [...pitches] : [];
    if (mainPageFacility) {
      const selectedFacility = facilities.find(f => f._id === mainPageFacility);
      if (selectedFacility) {
        filtered = filtered.filter(
          (pitch) => pitch.facilityName === selectedFacility.name
        );
      }
    }
    if (mainPageSport) {
      const selectedSport = sports.find(s => s._id === mainPageSport);
      if (selectedSport) {
        filtered = filtered.filter(
          (pitch) => pitch.sportName === selectedSport.name
        );
      }
    }
    return filtered.sort((a, b) => b._id.localeCompare(a._id));
  };

  // Get current pitches for pagination
  const getCurrentPitches = () => {
    const filteredPitches = getFilteredPitches();
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredPitches.slice(indexOfFirstItem, indexOfLastItem);
  };

  const totalPages = Math.ceil(getFilteredPitches().length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFilterClick = () => {
    setIsFiltering(true);
    setCurrentPage(1);
    setTimeout(() => setIsFiltering(false), 300);
  };

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setShowModal(false);
    setFormData({
      facilityId: "",
      sportId: "",
      name: "",
      maxPlayers: "",
    });
    dispatch(setSelectedFacility(null));
    dispatch(setSelectedSport(null));
  };

  const handleEditShow = (pitch) => {
    setEditFormData({
      _id: pitch._id,
      facilityId: pitch.facility,
      sportId: pitch.sport,
      name: pitch.name,
      maxPlayers: pitch.maxPlayers,
      facilityName: pitch.facilityName,
      sportName: pitch.sportName,
    });
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setEditFormData({
      _id: "",
      facilityId: "",
      sportId: "",
      name: "",
      maxPlayers: "",
      facilityName: "",
      sportName: "",
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const pitchData = {
        name: formData.name,
        maxPlayers: parseInt(formData.maxPlayers),
        facility: formData.facilityId,
        sport: formData.sportId,
      };
      await dispatch(addPitch(pitchData)).unwrap();
      await dispatch(fetchPitches());
      toast.success("Pitch added successfully!");
      handleClose();
    } catch (error) {
      console.error("Failed to add pitch:", error);
      toast.error(error.message || "Failed to add pitch. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const pitchData = {
        _id: editFormData._id,
        name: editFormData.name,
        maxPlayers: parseInt(editFormData.maxPlayers),
      };
      await editPitchService(pitchData);
      dispatch(fetchPitches());
      toast.success("Pitch updated successfully!");
      handleEditClose();
    } catch (error) {
      console.error("Failed to edit pitch:", error);
      toast.error(error.message || "Failed to edit pitch. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this pitch?")) {
      setIsLoading(true);
      try {
        await dispatch(deletePitch(id)).unwrap();
        dispatch(fetchPitches());
        toast.success("Pitch deleted successfully!");
      } catch (error) {
        toast.error(error.message || "Failed to delete pitch. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderSkeletons = () => (
    <Row className="pitch-grid">
      {Array.from({ length: 8 }).map((_, index) => (
        <SkeletonPitchCard key={`skeleton-${index}`} />
      ))}
    </Row>
  );

  const renderEmpty = () => (
    <Col md={12} className="d-flex justify-content-center" style={{ marginTop: "5%", marginBottom: "15%" }}>
      <Card className="text-center p-5">
        <Card.Body>
          <div style={{
            width: "50px",
            height: "50px",
            margin: "0 auto 20px auto",
            backgroundColor: "#f5f5f5",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px"
          }}>
            🏟️
          </div>
          <Card.Title>No Pitches Available</Card.Title>
          <Card.Text className="text-muted">
            {status === "failed"
              ? "Unable to load pitches. Please check your internet connection and try again."
              : "There are no pitches matching your current filters. Try changing your filter criteria."}
          </Card.Text>
          {status === "failed" && (
            <Button
              variant="outline-success"
              onClick={() => {
                dispatch(fetchPitches());
                dispatch(fetchFacilitiesForFilters());
                dispatch(fetchSportsForFilters());
              }}
            >
              Retry
            </Button>
          )}
        </Card.Body>
      </Card>
    </Col>
  );

  return (
    <Container className="facilities-container">
      <Row>
        <h1 className="container-title">View Pitch/Court</h1>
        <Col className="text-end">
          <Button variant="success" onClick={handleShow} disabled={isLoading}>
            + Add pitch/court
          </Button>
        </Col>
      </Row>

      <Row className="mb-4 dropdownscourt">
        <Col md={3}>
          <Form.Select
            value={mainPageFacility}
            onChange={handleMainPageFacilityChange}
            disabled={isLoading}
          >
            <option value="">Select facility</option>
            {facilities.map((facility) => (
              <option key={facility._id} value={facility._id}>
                {facility.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={mainPageSport}
            onChange={handleMainPageSportChange}
            disabled={isLoading}
          >
            <option value="">Select sports</option>
            {getMainPageFilteredSports().map((sport) => (
              <option key={sport._id} value={sport._id}>
                {sport.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button
            variant="success"
            className="w-100"
            onClick={handleFilterClick}
            disabled={isLoading}
          >
            Filter
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        renderSkeletons()
      ) : getCurrentPitches().length === 0 ? (
        renderEmpty()
      ) : (
        <Row className="pitch-grid">
          {getCurrentPitches().map((pitch) => (
            <Col lg={3} md={6} sm={12} key={pitch._id}>
              <Card className="pitch-card">
                <Card.Body>
                  <div className="d-flex justify-content-between mb-2">
                    <div className="pitch-info">
                      <h3 className="pitch-title">{pitch.name}</h3>
                      <p className="pitch-info-text">Facility: {pitch.facilityName || "N/A"}</p>
                    </div>
                    <div className="d-flex flex-column align-items-center">
                      <div className="sport-icon-container">
                        <img
                          src={sportIcons[pitch.sportName] || sportIcons["cross"] || sportImg}
                          alt={pitch.sportName || "Sport"}
                          style={{ width: "60%", height: "60%" }}
                        />
                      </div>
                      <div className="sport-icon-text">{pitch.sportName || "N/A"}</div>
                    </div>
                  </div>
                  <div className="action-buttons">
                    <Button
                      variant="success"
                      className="edit-btn"
                      onClick={() => handleEditShow(pitch)}
                    >
                      <img
                        src={EditIcon}
                        alt="Edit"
                        className="button-icon"
                      />
                      Edit
                    </Button>
                    <Button
                      variant="outline-light"
                      className="delete-btn"
                      onClick={() => handleDelete(pitch._id)}
                    >
                      <img
                        src={TrashIcon}
                        alt="Delete"
                        className="button-icon"
                      />
                      Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {!isLoading && totalPages > 1 && (
        <Row className="mt-4">
          <Col className="d-flex justify-content-center">
            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li
                    key={index}
                    className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </Col>
        </Row>
      )}

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Pitch/Court</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Facility</Form.Label>
              <Form.Select
                name="facilityId"
                value={formData.facilityId}
                onChange={handleModalFacilityChange}
                required
              >
                <option value="">Select facility</option>
                {facilities.map((facility) => (
                  <option key={facility._id} value={facility._id}>
                    {facility.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sport</Form.Label>
              <Form.Select
                name="sportId"
                value={formData.sportId}
                onChange={handleModalSportChange}
                required
              >
                <option value="">Select sport</option>
                {getModalFilteredSports().map((sport) => (
                  <option key={sport._id} value={sport._id}>
                    {sport.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Pitch/Court Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter pitch/court name"
                name="name"
                maxLength={25}
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Maximum Players</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter max players"
                name="maxPlayers"
                value={formData.maxPlayers}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Button variant="success" type="submit" className="addpitchbtn">
              Add Pitch
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={handleEditClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Pitch/Court</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Facility</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.facilityName}
                disabled
                className="disabled-field"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sport</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.sportName}
                disabled
                className="disabled-field"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Pitch/Court Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter pitch/court name"
                name="name"
                value={editFormData.name}
                onChange={handleEditChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Maximum Players</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter max players"
                name="maxPlayers"
                value={editFormData.maxPlayers}
                onChange={handleEditChange}
                required
              />
            </Form.Group>
            <Button variant="success" type="submit" className="addpitchbtn">
              Save Changes
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <style jsx>{`
        .disabled-field {
          background-color: #f8f9fa;
          color: #495057;
          cursor: not-allowed;
          border: 1px solid #dee2e6;
        }

        .disabled-field:focus {
          box-shadow: none;
          border-color: #dee2e6;
        }
      `}</style>
    </Container>
  );
};

export default ViewPitch;